import { useEffect, useRef, useState } from "react";
import { Plus, Upload, Send, Copy, RefreshCw, FileText, CheckCircle2, Clock, Download, Trash2, BellRing, Mail } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { PageHead, Card } from "@/pages/app/AppShell";
import { api, errMsg, fmtDate, fmtDateTime, money, download } from "@/lib/api";

const STEPS = [["draft", "Draft"], ["sent", "Sent"], ["partially_signed", "Partially signed"], ["executed", "Executed"]];
const blank = { title: "", counterparty_company: "", counterparty_name: "", counterparty_email: "", value: "", start_date: "", end_date: "", sign_deadline: "", reminder_days: 30, auto_renew: false, terms: "" };

const daysLeft = (d) => (d ? Math.ceil((new Date(`${d.slice(0, 10)}T00:00`) - new Date()) / 86400000) : null);

function Stepper({ status }) {
  const idx = STEPS.findIndex(([k]) => k === status);
  return (
    <div className="flex items-center gap-1.5" data-testid="agreement-stepper">
      {STEPS.map(([k, l], i) => (
        <div key={k} className="flex items-center gap-1.5">
          <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${i <= idx ? (k === "executed" ? "bg-emerald-400 text-white" : "bg-[#E0218A] text-white") : "bg-[#2A1240]/5 text-slate-400"}`}>{l}</span>
          {i < STEPS.length - 1 && <span className={`h-px w-3 ${i < idx ? "bg-[#E0218A]" : "bg-[#2A1240]/15"}`} />}
        </div>
      ))}
    </div>
  );
}

function AgreementCard({ a, reload }) {
  const fileRef = useRef(null);
  const left = daysLeft(a.end_date);
  const signLink = (t) => `${window.location.origin}/sign/${t}`;
  const act = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const upload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    act(() => api.post(`/agreements/${a.id}/documents`, fd), "Document uploaded");
    e.target.value = "";
  };
  const copy = (t) => { navigator.clipboard?.writeText(signLink(t)); toast.success("Signing link copied"); };
  return (
    <Card className="p-6" data-testid={`agreement-card-${a.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <p className="text-xs text-slate-400">{a.counterparty_company || a.counterparty_name} · <span className="font-mono">{money(a.value)}</span></p>
          <h3 className="mt-1 font-display text-2xl text-[#2A1240]">{a.title}</h3>
          <p className="mt-1 text-sm text-slate-400">{fmtDate(a.start_date) || "—"} → {fmtDate(a.end_date) || "—"}{a.auto_renew && " · auto-renew"}</p>
        </div>
        <Stepper status={a.status} />
      </div>
      {left !== null && left <= (a.reminder_days || 30) && (
        <div className={`mt-4 flex items-center gap-2 rounded-xl px-4 py-3 text-sm ${left < 0 ? "bg-red-400/10 text-red-200 border border-red-400/30" : "bg-amber-300/10 text-amber-200 border border-amber-300/30"}`} data-testid={`agreement-expiry-banner-${a.id}`}>
          <BellRing className="h-4 w-4" />{left < 0 ? `Expired ${-left} days ago` : `Expires in ${left} day${left === 1 ? "" : "s"}`} — renewal reminder
        </div>
      )}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <div>
          <p className="kicker mb-2">Signatures</p>
          {a.signers.map((s) => (
            <div key={s.role} className="flex items-center gap-2 py-1.5 text-sm" data-testid={`signer-${a.id}-${s.role}`}>
              {s.signed_at ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : <Clock className="h-4 w-4 text-slate-400" />}
              <span className="text-[#2A1240]">{s.name}</span>
              <span className="text-slate-400 text-xs">{s.signed_at ? `signed ${fmtDateTime(s.signed_at)}` : s.token ? "awaiting" : "not sent"}</span>
              {s.token && !s.signed_at && <button onClick={() => copy(s.token)} className="ml-auto text-xs text-[#8E2BC4] flex items-center gap-1 hover:underline" data-testid={`copy-sign-link-${a.id}-${s.role}`}><Copy className="h-3 w-3" />Copy link</button>}
            </div>
          ))}
        </div>
        <div>
          <p className="kicker mb-2">Documents</p>
          {!a.documents?.length && <p className="text-sm text-slate-400">No files uploaded.</p>}
          {(a.documents || []).map((d) => (
            <button key={d.id} onClick={() => download(`/documents/${d.id}/download`, d.original_filename)} className="flex items-center gap-2 py-1 text-sm text-[#8E2BC4] hover:underline" data-testid={`agreement-doc-${d.id}`}>
              <FileText className="h-4 w-4" />{d.original_filename}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-2">
        <input type="file" ref={fileRef} className="hidden" onChange={upload} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" data-testid={`agreement-file-input-${a.id}`} />
        <button className="btn-ghost !py-2 !px-4 !text-xs" onClick={() => fileRef.current?.click()} data-testid={`agreement-upload-${a.id}`}><Upload className="h-3.5 w-3.5" />Upload contract</button>
        {a.status === "draft" && <button className="btn-gold !py-2 !px-4 !text-xs" onClick={() => act(async () => { const { data } = await api.post(`/agreements/${a.id}/send`); toast.success(data?.emailed?.length ? `Signing links emailed to ${data.emailed.join(", ")}` : "Signing links created — copy and share them with each signer"); })} data-testid={`agreement-send-${a.id}`}><Send className="h-3.5 w-3.5" />Send for signature</button>}
        {["sent", "partially_signed"].includes(a.status) && <button className="btn-ghost !py-2 !px-4 !text-xs" onClick={() => act(async () => { const { data } = await api.post(`/agreements/${a.id}/remind`); toast.success(`Reminder emailed to ${(data?.emailed || []).join(", ")}`); })} data-testid={`agreement-remind-${a.id}`}><Mail className="h-3.5 w-3.5" />Email reminder</button>}
        {a.end_date && <button className="btn-ghost !py-2 !px-4 !text-xs" onClick={() => act(() => api.post(`/agreements/${a.id}/renew`), "Agreement renewed")} data-testid={`agreement-renew-${a.id}`}><RefreshCw className="h-3.5 w-3.5" />Renew term</button>}
        <label className="flex items-center gap-2 text-xs text-slate-300 ml-auto">Remind
          <select value={a.reminder_days} onChange={(e) => act(() => api.patch(`/agreements/${a.id}`, { reminder_days: Number(e.target.value) }), "Reminder updated")} className="rounded-lg bg-[#FBE9F3] border border-[#2A1240]/10 px-2 py-1" data-testid={`agreement-reminder-${a.id}`}>
            {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{`${d} days before`}</option>)}
          </select>
        </label>
      </div>
      {a.history?.length > 0 && (
        <details className="mt-4 text-xs text-slate-400">
          <summary className="cursor-pointer">Audit trail ({a.history.length})</summary>
          <ul className="mt-2 space-y-1">{a.history.map((h, i) => <li key={i}>{fmtDateTime(h.at)} — {h.event}</li>)}</ul>
        </details>
      )}
    </Card>
  );
}

function DocumentVault() {
  const [docs, setDocs] = useState([]);
  const ref = useRef(null);
  const load = () => api.get("/documents").then((r) => setDocs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/documents", fd);
      toast.success("Stored in vault");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
    e.target.value = "";
  };
  const del = async (id) => { await api.delete(`/documents/${id}`).catch(() => {}); load(); };
  return (
    <Card className="p-6" data-testid="document-vault">
      <div className="flex items-center justify-between">
        <p className="kicker">All documents</p>
        <input type="file" ref={ref} className="hidden" onChange={upload} data-testid="vault-file-input" />
        <button className="btn-ghost !py-2 !px-4 !text-xs" onClick={() => ref.current?.click()} data-testid="vault-upload-button"><Upload className="h-3.5 w-3.5" />Upload</button>
      </div>
      {!docs.length && <p className="mt-6 text-sm text-slate-400">No documents stored yet.</p>}
      <div className="mt-4 divide-y divide-[#2A1240]/10">
        {docs.map((d) => (
          <div key={d.id} className="py-3 flex items-center gap-3 text-sm" data-testid={`vault-doc-${d.id}`}>
            <FileText className="h-4 w-4 text-[#E0218A]" />
            <span className="flex-1 truncate text-[#2A1240]">{d.original_filename}</span>
            <span className="hidden sm:block text-xs text-slate-400">{d.agreement_title || d.scope} · {(d.size / 1024).toFixed(0)} KB · {fmtDate(d.created_at)}</span>
            <button onClick={() => download(`/documents/${d.id}/download`, d.original_filename)} className="text-slate-300 hover:text-[#2A1240]" aria-label="Download" data-testid={`vault-download-${d.id}`}><Download className="h-4 w-4" /></button>
            <button onClick={() => del(d.id)} className="text-slate-400 hover:text-red-300" aria-label="Delete" data-testid={`vault-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </Card>
  );
}

export default function Agreements() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const load = () => api.get("/agreements").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  const create = async () => {
    try {
      await api.post("/agreements", { ...f, value: Number(f.value || 0), reminder_days: Number(f.reminder_days) });
      toast.success("Agreement drafted");
      setOpen(false);
      setF(blank);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div data-testid="agreements-page">
      <PageHead kicker="Contracts · signatures · renewals" title="Agreements">
        <button className="btn-gold !py-2.5" onClick={() => setOpen(true)} data-testid="new-agreement-button"><Plus className="h-4 w-4" />New agreement</button>
      </PageHead>
      <Tabs defaultValue="agreements">
        <TabsList className="bg-[#FFFFFF] border border-[#2A1240]/10">
          <TabsTrigger value="agreements" data-testid="tab-agreements">Agreements</TabsTrigger>
          <TabsTrigger value="vault" data-testid="tab-vault">Document vault</TabsTrigger>
        </TabsList>
        <TabsContent value="agreements" className="mt-6 space-y-5">
          {!items.length && <p className="rounded-2xl border border-dashed border-[#2A1240]/15 p-10 text-center text-slate-300" data-testid="agreements-empty">No agreements yet. Draft your first partnership agreement.</p>}
          {items.map((a) => <AgreementCard key={a.id} a={a} reload={load} />)}
        </TabsContent>
        <TabsContent value="vault" className="mt-6"><DocumentVault /></TabsContent>
      </Tabs>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#2A1240]/10 max-w-xl max-h-[90vh] overflow-y-auto" data-lenis-prevent data-testid="agreement-dialog">
          <DialogTitle className="font-display text-2xl font-normal">New partnership agreement</DialogTitle>
          <input className="field" placeholder="Agreement title" value={f.title} onChange={set("title")} data-testid="agreement-title-input" />
          <input className="field" placeholder="Counterparty company" value={f.counterparty_company} onChange={set("counterparty_company")} data-testid="agreement-company-input" />
          <div className="grid grid-cols-2 gap-3">
            <input className="field" placeholder="Signer name" value={f.counterparty_name} onChange={set("counterparty_name")} data-testid="agreement-signer-name-input" />
            <input className="field" type="email" placeholder="Signer email" value={f.counterparty_email} onChange={set("counterparty_email")} data-testid="agreement-signer-email-input" />
            <input className="field" type="number" placeholder="Contract value ($)" value={f.value} onChange={set("value")} data-testid="agreement-value-input" />
            <label className="text-xs text-slate-400">Signature deadline<input className="field mt-1" type="date" value={f.sign_deadline} onChange={set("sign_deadline")} data-testid="agreement-deadline-input" /></label>
            <label className="text-xs text-slate-400">Start date<input className="field mt-1" type="date" value={f.start_date} onChange={set("start_date")} data-testid="agreement-start-input" /></label>
            <label className="text-xs text-slate-400">Expiration date<input className="field mt-1" type="date" value={f.end_date} onChange={set("end_date")} data-testid="agreement-end-input" /></label>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="text-xs text-slate-400 flex items-center gap-2">Renewal reminder
              <select className="rounded-lg bg-[#FBE9F3] border border-[#2A1240]/10 px-2 py-1" value={f.reminder_days} onChange={set("reminder_days")} data-testid="agreement-reminder-input">{[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{`${d} days before`}</option>)}</select>
            </label>
            <label className="text-xs text-slate-300 flex items-center gap-2"><input type="checkbox" checked={f.auto_renew} onChange={set("auto_renew")} className="accent-[#E0218A]" data-testid="agreement-autorenew-input" />Auto-renew</label>
          </div>
          <textarea className="field" rows={5} placeholder="Key terms (deliverables, exclusivity, payment schedule)" value={f.terms} onChange={set("terms")} data-testid="agreement-terms-input" />
          <button className="btn-gold" disabled={!f.title || !f.counterparty_name || !f.counterparty_email} onClick={create} data-testid="agreement-save-button">Create draft</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
